// Considered: ArcStageInspectorMolecule — that composes TypeFieldRowMolecule for a STAGE's
// fields (key date, offset, requirements); this composes the same molecule for the ARC's own
// fields (name, description, key dates, settings) plus ApplicabilityEditorMolecule, which no
// stage-level editor needs. Not a fork of the stage editor: the two share no fields. This
// component has no prototype panel to build from (`arc-inspector.jsx`'s `ArcInspector` switches
// only on stage/transition/intent/action) — it is an ADDITION beyond the prototype, per the
// design interview's "Arc inspector on header select" resolution (D24,
// .ralph/IMPLEMENTATION_PLAN.md). See spec Reuse Audit (arc-editor-canvas-v1.md).
import React from 'react';
import { useTranslation } from 'localization';
import type { Button } from 'primereact/button';
import type { ArcArchetype } from 'types/arc';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import TypeFieldRowMolecule from '../studio/TypeFieldRowMolecule';
import InlineConfirmMolecule from '../studio/InlineConfirmMolecule';
import { focusPrimeButton } from '../../../utils/focusPrimeButton';
import ApplicabilityEditorMolecule, {
  type ApplicabilityEditorMoleculeProps,
} from '../ApplicabilityEditor/ApplicabilityEditorMolecule';
import './ArcTemplateInspectorMolecule.scss';

export interface ArcTemplateInspectorKeyDateRow {
  name: string;
  expression: string;
}

/** `^(parent|sphere)\.\w+$` — the engine's own key-date anchor grammar (spec D24). */
const KEY_DATE_EXPRESSION_PATTERN = /^(parent|sphere)\.\w+$/;

export interface ArcTemplateInspectorMoleculeProps {
  name: string;
  onNameChange: (value: string) => void;
  description: string;
  onDescriptionChange: (value: string) => void;
  /** Fixed at creation (the gallery's New Arc dialog); shown so the author knows which stage
   * shape the server will accept — a terminal stage on an `evolving` arc is refused on save. */
  archetype: ArcArchetype;
  applicability: Omit<ApplicabilityEditorMoleculeProps, 'className'>;
  keyDates: ArcTemplateInspectorKeyDateRow[];
  onAddKeyDate: () => void;
  onKeyDateNameChange: (name: string, value: string) => void;
  onKeyDateExpressionChange: (name: string, value: string) => void;
  /** Called only after the row's inline confirm is accepted. */
  onRemoveKeyDate: (name: string) => void;
  reopenable: boolean;
  onReopenableChange: (value: boolean) => void;
  allowManualCancellation: boolean;
  onAllowManualCancellationChange: (value: boolean) => void;
  autoComplete: boolean;
  onAutoCompleteChange: (value: boolean) => void;
  editable: boolean;
  onApply: () => void;
  className?: string;
}

/** The arc-level editor the inspector rail's `selection.body` hosts for `selection.kind ===
 * 'arc'` (D24 — the header's arc name/glyph opens it; there is no prototype panel). */
const ArcTemplateInspectorMolecule: React.FC<ArcTemplateInspectorMoleculeProps> = ({
  name,
  onNameChange,
  description,
  onDescriptionChange,
  archetype,
  applicability,
  keyDates,
  onAddKeyDate,
  onKeyDateNameChange,
  onKeyDateExpressionChange,
  onRemoveKeyDate,
  reopenable,
  onReopenableChange,
  allowManualCancellation,
  onAllowManualCancellationChange,
  autoComplete,
  onAutoCompleteChange,
  editable,
  onApply,
  className = '',
}) => {
  const { t } = useTranslation();
  const ns = 'studio.arcEditor.inspector.arc';
  // Removing a key date is consequential (stages and transitions may anchor on it) — the row's
  // inline confirm says so in place; "Keep" returns focus to the button that opened it.
  const [confirmingKeyDate, setConfirmingKeyDate] = React.useState<string | null>(null);
  const keyDateRemoveRefs = React.useRef<Record<string, Button | null>>({});
  const keepKeyDate = (name: string) => {
    setConfirmingKeyDate(null);
    focusPrimeButton(keyDateRemoveRefs.current[name]);
  };
  const [nameTouched, setNameTouched] = React.useState(false);
  const nameError = nameTouched && !name.trim() ? t(`${ns}.nameRequired`) : undefined;

  return (
    <div className={`arc-template-inspector ${className}`.trim()}>
      <div className="arc-template-inspector__body">
        <TypeFieldRowMolecule
          label={t(`${ns}.nameLabel`)}
          layout="stacked"
          mode={editable ? 'edit' : 'disabled'}
          errorMessage={nameError}
          controls={[
            {
              kind: 'text',
              value: name,
              onChange: onNameChange,
              onCommit: () => setNameTouched(true),
              ariaLabel: t(`${ns}.nameLabel`),
            },
          ]}
        />
        <TypeFieldRowMolecule
          label={t(`${ns}.descriptionLabel`)}
          layout="stacked"
          mode={editable ? 'edit' : 'disabled'}
          controls={[
            {
              kind: 'multiline',
              value: description,
              placeholder: t(`${ns}.descriptionPlaceholder`),
              onChange: onDescriptionChange,
              ariaLabel: t(`${ns}.descriptionLabel`),
            },
          ]}
        />
        <TypeFieldRowMolecule
          label={t(`${ns}.archetypeLabel`)}
          secondary={t(`studio.arcEditor.archetypes.${archetype}.note`)}
          layout="stacked"
          mode="locked"
          lockReason="structural"
          data-testid="arc-template-inspector-archetype"
          controls={[
            {
              kind: 'text',
              value: t(`studio.arcEditor.archetypes.${archetype}.label`),
              ariaLabel: t(`${ns}.archetypeLabel`),
            },
          ]}
        />

        <section className="arc-template-inspector__section">
          <span className="arc-template-inspector__section-label">{t(`${ns}.applicabilityHeading`)}</span>
          <ApplicabilityEditorMolecule {...applicability} readOnly={!editable || applicability.readOnly} />
        </section>

        <section className="arc-template-inspector__section">
          <span className="arc-template-inspector__section-label">{t(`${ns}.keyDatesHeading`)}</span>
          {keyDates.length === 0 ? (
            <p className="arc-template-inspector__note">{t(`${ns}.keyDatesNoneNote`)}</p>
          ) : (
            <div className="arc-template-inspector__key-dates">
              {keyDates.map((row) => {
                const expressionInvalid =
                  row.expression.trim().length > 0 && !KEY_DATE_EXPRESSION_PATTERN.test(row.expression);
                return (
                  <div key={row.name} className="arc-template-inspector__key-date">
                    <span className="arc-template-inspector__key-date-head">
                      <GlyphAtom glyph="lucide:calendar-clock" aria-hidden />
                      {editable && (
                        <ButtonAtom
                          ref={(el) => {
                            keyDateRemoveRefs.current[row.name] = el;
                          }}
                          variant="text"
                          shape="icon-square"
                          size="small"
                          icon="pi pi-times"
                          aria-label={t(`${ns}.removeKeyDateAria`)}
                          onClick={() => setConfirmingKeyDate(row.name)}
                        />
                      )}
                    </span>
                    {editable && confirmingKeyDate === row.name && (
                      <InlineConfirmMolecule
                        tone="danger"
                        testId="arc-template-inspector-key-date-remove-confirm"
                        titleId={`arc-template-inspector-key-date-remove-confirm-${row.name}`}
                        title={t(`${ns}.removeKeyDateConfirm.title`, { name: row.name })}
                        body={t(`${ns}.removeKeyDateConfirm.body`)}
                        actions={[
                          {
                            label: t(`${ns}.removeKeyDateConfirm.accept`),
                            onClick: () => {
                              setConfirmingKeyDate(null);
                              onRemoveKeyDate(row.name);
                            },
                            variant: 'danger',
                            'data-testid': 'arc-template-inspector-key-date-remove-confirm-accept',
                          },
                          {
                            label: t(`${ns}.removeKeyDateConfirm.keep`),
                            onClick: () => keepKeyDate(row.name),
                            variant: 'text',
                            'data-testid': 'arc-template-inspector-key-date-remove-confirm-dismiss',
                          },
                        ]}
                      />
                    )}
                    <TypeFieldRowMolecule
                      label={t(`${ns}.keyDateNameLabel`)}
                      layout="stacked"
                      mode={editable ? 'edit' : 'disabled'}
                      controls={[
                        {
                          kind: 'text',
                          value: row.name,
                          onChange: (value) => onKeyDateNameChange(row.name, value),
                          ariaLabel: t(`${ns}.keyDateNameLabel`),
                        },
                      ]}
                    />
                    <TypeFieldRowMolecule
                      label={t(`${ns}.keyDateExpressionLabel`)}
                      hint={t(`${ns}.keyDateExpressionHint`)}
                      layout="stacked"
                      mode={editable ? 'edit' : 'disabled'}
                      errorMessage={expressionInvalid ? t(`${ns}.keyDateExpressionInvalid`) : undefined}
                      controls={[
                        {
                          kind: 'text',
                          value: row.expression,
                          placeholder: t(`${ns}.keyDateExpressionPlaceholder`),
                          onChange: (value) => onKeyDateExpressionChange(row.name, value),
                          ariaLabel: t(`${ns}.keyDateExpressionLabel`),
                        },
                      ]}
                    />
                  </div>
                );
              })}
            </div>
          )}
          {editable && (
            <ButtonAtom
              variant="text"
              size="small"
              icon={<GlyphAtom glyph="lucide:plus" aria-hidden />}
              onClick={onAddKeyDate}
            >
              {t(`${ns}.addKeyDate`)}
            </ButtonAtom>
          )}
        </section>

        <section className="arc-template-inspector__section">
          <span className="arc-template-inspector__section-label">{t(`${ns}.settingsHeading`)}</span>
          <TypeFieldRowMolecule
            label={t(`${ns}.reopenableLabel`)}
            hint={t(`${ns}.reopenableHint`)}
            layout="stacked"
            mode={editable ? 'edit' : 'disabled'}
            controls={[
              {
                kind: 'switch',
                checked: reopenable,
                onChange: onReopenableChange,
                ariaLabel: t(`${ns}.reopenableLabel`),
              },
            ]}
          />
          <TypeFieldRowMolecule
            label={t(`${ns}.allowManualCancellationLabel`)}
            layout="stacked"
            mode={editable ? 'edit' : 'disabled'}
            controls={[
              {
                kind: 'switch',
                checked: allowManualCancellation,
                onChange: onAllowManualCancellationChange,
                ariaLabel: t(`${ns}.allowManualCancellationLabel`),
              },
            ]}
          />
          <TypeFieldRowMolecule
            label={t(`${ns}.autoCompleteLabel`)}
            layout="stacked"
            mode={editable ? 'edit' : 'disabled'}
            controls={[
              {
                kind: 'switch',
                checked: autoComplete,
                onChange: onAutoCompleteChange,
                ariaLabel: t(`${ns}.autoCompleteLabel`),
              },
            ]}
          />
        </section>
      </div>
      {editable && (
        <div className="arc-template-inspector__actions">
          <ButtonAtom variant="secondary" size="small" onClick={onApply}>
            {t(`${ns}.apply`)}
          </ButtonAtom>
        </div>
      )}
    </div>
  );
};

ArcTemplateInspectorMolecule.displayName = 'ArcTemplateInspectorMolecule';

export default ArcTemplateInspectorMolecule;
